import { useCallback, useEffect, useRef, useState } from 'react';
import type { SentryHealthResponse } from '@/types/sentry-health.types';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-sentry-health`;

export type Status = 'idle' | 'loading' | 'success' | 'error';

export interface UseAdminSentryHealthResult {
  status: Status;
  data: SentryHealthResponse | null;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the edge monitoring verdict from the password-gated
 * `admin-sentry-health` function.
 *
 * There is no fallback value and no default-to-healthy branch: on failure
 * `data` stays null and `error` is set, so the card renders "unknown" rather
 * than inventing a status. The panel this replaces went green on an
 * unchecked constant, and a hook that quietly substituted an optimistic
 * default would put that behaviour straight back.
 */
export function useAdminSentryHealth(
  password: string,
): UseAdminSentryHealthResult {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<SentryHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res
          .json()
          .catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json: SentryHealthResponse = await res.json();
      setData(json);
      setStatus('success');
      setError(null);
    } catch (err) {
      // Drop any previous verdict: a stale "initialized" outliving the check
      // that produced it is the same lie in slower motion.
      setData(null);
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return { status, data, error, refetch: fetchOnce };
}
